"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import {
  LogIn,
  LogOut,
  User as UserIcon,
  KeyRound,
  UserCog,
  GraduationCap,
} from "lucide-react";
import { useSafeAuth } from "@/hooks/use-safe-auth";
import { ProfileSelectionDialog } from "@/components/profile/ProfileSelectionDialog";
import { useProfileCompletionPrompt } from "@/hooks/use-profile-completion-prompt";
import { useStaffProfilePrompt } from "@/hooks/use-staff-profile-prompt";
import { useStudentProfilePrompt } from "@/hooks/use-student-profile-prompt";
import { signOutAndRedirect } from "@/lib/logout-client";

/**
 * Mobile counterpart of the desktop `HeaderUserActions` dropdown. Most
 * users register on a phone, so the mobile header's user icon must expose
 * the same "complete / edit your profile" path — with the progress bar —
 * that the desktop dropdown does, instead of a bare link to `/profile`.
 *
 * Guest (not signed in) → compact LogIn icon → `/login` (keeps the
 * icon-only mobile chrome). Signed in → an avatar button that opens a
 * compact dropdown carrying the exact same completion prompts (student /
 * staff, mutually exclusive) + profile / password / sign-out actions. An
 * amber dot on the icon flags that something needs completing so the user
 * notices without opening the menu.
 */
export function MobileUserMenu({ language }: { language: "en" | "mm" }) {
  const { user, isAuthenticated, isLoading } = useSafeAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const profilePrompt = useProfileCompletionPrompt();
  const staffPrompt = useStaffProfilePrompt();
  const studentPrompt = useStudentProfilePrompt();

  const handleSignOut = useCallback(async () => {
    // Top-level nav to the OIDC end-session (see signOutAndRedirect) — a bare
    // fetch + go-home leaves the SameSite=Lax session cookie → silent re-login.
    await signOutAndRedirect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Reserve the slot during the auth probe so the header doesn't shift.
  if (isLoading) {
    return <div className="h-9 w-9" aria-hidden />;
  }

  // Not signed in → keep the compact LogIn icon (matches mobile chrome).
  if (!isAuthenticated || !user) {
    return (
      <Link
        href="/login"
        className="inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground hover:bg-muted/60 transition-colors"
        aria-label={language === "mm" ? "လော့ဂ်အင်ဝင်ရန်" : "Sign in"}
        title={language === "mm" ? "လော့ဂ်အင်ဝင်ရန်" : "Sign in"}
      >
        <LogIn size={18} />
      </Link>
    );
  }

  const userName = (user as any)?.name || "";
  const userEmail = (user as any)?.email || "";
  const initials =
    (userName || userEmail || "U")
      .split(/\s+/)
      .map((s: string) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  // Mutual exclusion: student prompt wins over staff (approval-gated → higher
  // cost to hide) — same rule as the desktop dropdown.
  const showStudentPrompt = studentPrompt.needsCompletion;
  const showStaffPrompt = staffPrompt.needsCompletion && !showStudentPrompt;
  const needsAttention =
    showStudentPrompt || showStaffPrompt || profilePrompt.needsCompletion;

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground hover:bg-muted/60 transition-colors"
          aria-label={userName || userEmail || "Profile"}
          title={userName || userEmail || "Profile"}
        >
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold"
            style={{ backgroundColor: "var(--color-primary)", color: "#FFFFFF" }}
          >
            {initials}
          </span>
          {needsAttention && (
            <span
              className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background"
              aria-hidden
            />
          )}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-full mt-2 w-64 rounded-md border border-border bg-background shadow-xl z-[70]"
            style={{ borderTopWidth: 3, borderTopColor: "var(--color-primary)" }}
          >
            <div className="px-3 py-2 border-b border-border">
              <div className="text-sm font-semibold text-foreground truncate">
                {userName || userEmail}
              </div>
              {userName && userEmail && (
                <div className="text-xs text-muted-foreground truncate">
                  {userEmail}
                </div>
              )}
            </div>
            <ul className="py-1">
              {profilePrompt.needsCompletion && (
                <>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        profilePrompt.setOpen(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 hover:text-primary transition-colors text-left"
                    >
                      <UserCog className="h-4 w-4" />
                      {language === "mm"
                        ? "ပရိုဖိုင် ဖြည့်စွက်ရန်"
                        : "Complete profile"}
                    </button>
                  </li>
                  <li className="border-t border-border my-1" />
                </>
              )}
              {showStudentPrompt && (
                <>
                  <li className="px-3 py-2.5">
                    <Link
                      href="/profile/student"
                      onClick={() => setOpen(false)}
                      className="block group"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                          {language === "mm"
                            ? "ကျောင်းသား ပရိုဖိုင် ဖြည့်စွက်ရန်"
                            : "Complete student profile"}
                        </span>
                        <span
                          className="text-xs font-semibold"
                          style={{ color: "var(--color-primary)" }}
                        >
                          {studentPrompt.percentage}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${studentPrompt.percentage}%`,
                            backgroundColor: "var(--color-primary)",
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight">
                        {language === "mm"
                          ? "အတည်ပြုမခံရမီ ပြင်ဆင်/ဖြည့်စွက်နိုင်သည်"
                          : "Edit / complete until it's approved"}
                      </p>
                    </Link>
                  </li>
                  <li className="border-t border-border my-1" />
                </>
              )}
              {showStaffPrompt && (
                <>
                  <li className="px-3 py-2.5">
                    <Link
                      href="/profileSetup/staff?mode=full"
                      onClick={() => setOpen(false)}
                      className="block group"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                          {language === "mm"
                            ? "ပရိုဖိုင် ဖြည့်စွက်ရန်"
                            : "Complete your profile"}
                        </span>
                        <span
                          className="text-xs font-semibold"
                          style={{ color: "var(--color-primary)" }}
                        >
                          {staffPrompt.percentage}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${staffPrompt.percentage}%`,
                            backgroundColor: "var(--color-primary)",
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight">
                        {language === "mm"
                          ? "အချိန်ရရင် ဆက်ပြီး ဖြည့်စွက်ပါ"
                          : "Finish when you have time"}
                      </p>
                    </Link>
                  </li>
                  <li className="border-t border-border my-1" />
                </>
              )}
              <li>
                <Link
                  href="/profile"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 hover:text-primary transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <UserIcon className="h-4 w-4" />
                  {language === "mm" ? "ပရိုဖိုင်" : "Profile"}
                </Link>
              </li>
              {studentPrompt.hasProfile && !studentPrompt.needsCompletion && (
                <li>
                  <Link
                    href="/profile/student"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 hover:text-primary transition-colors"
                    onClick={() => setOpen(false)}
                  >
                    <GraduationCap className="h-4 w-4" />
                    {language === "mm" ? "ကျောင်းသား ပရိုဖိုင်" : "Student Profile"}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  href="/profile?action=change-password"
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 hover:text-primary transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <KeyRound className="h-4 w-4" />
                  {language === "mm" ? "စကားဝှက် စီမံရန်" : "Password settings"}
                </Link>
              </li>
              <li className="border-t border-border my-1" />
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    handleSignOut();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 hover:text-primary transition-colors text-left"
                >
                  <LogOut className="h-4 w-4" />
                  {language === "mm" ? "ထွက်ရန်" : "Sign out"}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      <ProfileSelectionDialog
        open={profilePrompt.open}
        onOpenChange={profilePrompt.setOpen}
        user={profilePrompt.user}
      />
    </>
  );
}

export default MobileUserMenu;
